import { useEffect, useState, useRef, useCallback } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { GlobalSetting } from 'interfaces/globalSetting.interface';
import { GlobalSettingsService } from 'services/globalSettings.service';
import { GlobalService } from 'services/global.service';
import Page from '@common_components/global/page.component';import { KEY } from 'constants/local_storage_key.constant';
import { UUID, Client, Site, CommonClientService, CommonSiteService, RootState, usePersistantState, LoggerService, Sector, CommonSectorService, CommonGlobalService } from '@umanforge/common';
import ClientSelector from '@common_components/layout/clientSelector.component';
import Tabs, { TabItem } from '@common_components/buttons/Tabs.component';
import SettingsSite from 'components/settings/settingsSite.component';
import SettingsClient from 'components/settings/settingsClient.component';
import DataChecker from 'components/common/dataChecker';
import { Loading } from '@common_components/loading.component';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import { KEYFRAME } from 'constants/setting.constants';
import SettingsSector from 'components/settings/settingsSector.component';

const logger: LoggerService = new LoggerService( 'SettingsView' );
const ParametersView: React.FC = () => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );

  const popupRef = useRef( null );
  const buttonRef = useRef( null );
  const initialGroupedSettingsSnapshotRef = useRef<string | null>( null );

  const [ selectedClientId, setSelectedClientId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_CLIENT_ID, undefined );
  const [ selectedSiteId, setSelectedSiteId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SITE_ID, undefined );
  const [ selectedSectorId, setSelectedSectorId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );

  const [ allClients, setAllClients ] = useState<Client[]>( [] );
  const [ allSites, setAllSites ] = useState<Site[]>( [] );
  const [ allSectors, setAllSectors ] = useState<Sector[]>( [] );

  const [ defaultGroupedSettings, setDefaultGroupedSettings ] = useState<{ calendar: GlobalSetting[]; eventManager: GlobalSetting[]; }>( { calendar: [], eventManager: [], } );
  const [ groupedSettings, setGroupedSettings ] = useState<{ calendar: GlobalSetting[]; eventManager: GlobalSetting[]; }>( { calendar: [], eventManager: [], } );
  const [ isFirstRender, setIsFirstRender ] = useState( true );
  const [ isSaving, setIsSaving ] = useState( false );
  const [ canSave, setCanSaving ] = useState( false );

  const [ isClientSelectorPopupVisible, setClientSelectorPopupVisible ] = useState( false );
  const [ isConfirmLeaveOpen, setIsConfirmLeaveOpen ] = useState<boolean>( false );
  const [ pendingHref, setPendingHref ] = useState<string | null>( null );

  const [ pages, setPages ] = useState<TabItem[]>( [] );
  const [ selectedPage, setSelectedPage ] = useState<number>( 0 );

  const [ customSiteSettings, setCustomSiteSettings ] = useState( [
    { name: '', type: '', constraint: '' }
  ] );

  const isClientAdmin = React.useMemo( () => {
    if ( !me ) {
      return false;
    }

    return me.isSuperAdmin || !!me.clients?.some( c => {
      return c.isAdmin;
    } );
  }, [ me, selectedClientId ] );

  const isSiteAdmin = React.useMemo( () => {
    if ( !me ) {
      return false;
    }
    return me.isSuperAdmin || !!me.clients?.some( c => {
      return (
        c.isAdmin ||
        c.sites?.some( s => {
          return s.isAdmin;
        } )
      );
    } );
  }, [ me ] );

  const isSectorAdmin = React.useMemo( () => {
    if ( !me ) {
      return false;
    }
    return me.isSuperAdmin || !!me.clients?.some( c => {
      return (
        c.isAdmin ||
        c.sites?.some( s => {
          return (
            s.isAdmin ||
            // eslint-disable-next-line max-nested-callbacks
            s.sectors?.some( sec => {
              return sec.isAdmin;
            } )
          );
        } )
      );
    } );
  }, [ me ] );

  useEffect( () => {
    if ( !me || !selectedClientId || !selectedSiteId || !selectedSectorId ) {
      return;
    }
    if ( !isSectorAdmin && !isSiteAdmin && !isClientAdmin ) {
      navigate( '/' );
      return;
    }

    inits();
  }, [ navigate, me, isSiteAdmin, isClientAdmin, isSectorAdmin, selectedClientId, selectedSiteId, selectedSectorId ] );

  useEffect( () => {
    if ( !isFirstRender && !isValidClientId() ) {
      logger.warn( 'No valid client selected for settings page' );
    }
  }, [ selectedClientId, navigate, isFirstRender ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const filteredPages = [];

      if ( isClientAdmin ) {
        const hasGlobalSettings = Object.values( groupedSettings ).some( arr => { return arr.length > 0; } );
        if ( hasGlobalSettings ) {
          filteredPages.push( { id: 'globalSettings', name: t( 'common:globalSettings' ) } );
        }
      }
      if ( isSiteAdmin && selectedSiteId != null ) {
        filteredPages.push( { id: 'site_settings', name: t( 'translation:site_settings' ) } );
      }
      if ( isSectorAdmin && selectedSectorId != null ) {
        filteredPages.push( { id: 'sector_settings', name: t( 'translation:sector_settings' ) } );
      }

      setPages( filteredPages );
      if ( filteredPages.length > 0 && selectedPage >= filteredPages.length ) {
        setSelectedPage( filteredPages.length - 1 );
      }
    }
  }, [ selectedClientId, selectedSiteId, selectedSectorId, isFirstRender, isClientAdmin, isSiteAdmin, isSectorAdmin, groupedSettings, t ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const fetchSettings = async () => {
        if ( !isValidClientId() ) {
          logger.warn( 'Cannot fetch settings: invalid or missing selectedClientId' );
          return;
        }

        try {
          const fetchedSettings = await GlobalSettingsService.getGlobalSettings(
            selectedClientId!,
            token,
            dispatch
          ) as GlobalSetting[];
          const grouped = {
            calendar: fetchedSettings
              .filter( setting =>
              {return [ KEYFRAME.REFESH_DELAY_VIEW_MODE, KEYFRAME.CALENDAR_COLUMN ].includes( setting.keyframe );}
              )
              .map( setting => {return {
                ...setting,
                label: t( `common:${setting.keyframe}` )
              };} ),
            eventManager: fetchedSettings
              .filter( setting =>
              {return [ KEYFRAME.DAYS_DISPLAY, KEYFRAME.CAN_CREATE_FROM_BOARD ].includes( setting.keyframe );}
              )
              .map( setting => {return {
                ...setting,
                label: t( `common:${setting.keyframe}` )
              };} )
              .sort( ( a, b ) => {
                const order: { [key: string]: number } = { [ KEYFRAME.DAYS_DISPLAY ]: 0, [ KEYFRAME.CAN_CREATE_FROM_BOARD ]: 1 };
                return ( order[ a.keyframe ] ?? 999 ) - ( order[ b.keyframe ] ?? 999 );
              } ),
          };
          setGroupedSettings( grouped );
          setDefaultGroupedSettings( grouped );
          try {
            initialGroupedSettingsSnapshotRef.current = buildSettingsSnapshot( grouped );
          } catch ( e ) {
            logger.warn( 'Could not snapshot grouped settings', e );
          }

        } catch ( error ) {
          logger.error( 'Error fetching settings:', error );
          toast.error( t( 'common:error.fetching_settings' ) );
        }
      };
      fetchSettings();
    }
  }, [ selectedClientId, token, dispatch, t, isFirstRender ] );

  useEffect( () => {
    if ( selectedClientId && GlobalService.isValidGuid( selectedClientId ) ) {
      CommonSiteService.getAllowedSitesByClientsId( [ selectedClientId ], token, dispatch )
        .then( sitesFetched => {
          setAllSites( sitesFetched );
          if ( !sitesFetched.some( site => { return site.id === selectedSiteId; } ) ) {
            const firstSite = sitesFetched.length > 0 ? sitesFetched[ 0 ] : null;
            setSelectedSiteId( firstSite?.id ?? undefined );
          }
        } );
    } else {
      setAllSites( [] );
      setSelectedSiteId( undefined );
    }
  }, [ selectedClientId, token, dispatch ] );

  useEffect( () => {
    if ( selectedSiteId && GlobalService.isValidGuid( selectedSiteId ) ) {
      CommonSectorService.getAllowedSectorsBySitesId( [ selectedSiteId ], token, dispatch )
        .then( sectorsFetched => {
          setAllSectors( sectorsFetched );
          if ( !sectorsFetched.some( sector => { return sector.id === selectedSectorId; } ) ) {
            const firstSector = sectorsFetched.length > 0 ? sectorsFetched[ 0 ] : null;
            setSelectedSectorId( firstSector?.id ?? undefined );
          }
        } );
    } else {
      setAllSectors( [] );
      setSelectedSectorId( undefined );
    }
  }, [ selectedSiteId, token, dispatch ] );

  // listen to global navigation attempts from other components (left bar)
  useEffect( () => {
    interface NavigateAttemptDetail { path: string; handled?: boolean }
    const onNavigateAttempt = ( e: Event ) => {
      const ce = e as CustomEvent<NavigateAttemptDetail>;
      const detail = ce?.detail;
      if ( !detail || typeof detail.path !== 'string' ) { return; }

      try {
        if ( initialGroupedSettingsSnapshotRef.current == null ) {
          return;
        }
        const current = buildSettingsSnapshot( groupedSettings );
        if ( current === initialGroupedSettingsSnapshotRef.current ) {
          // no changes -> allow navigation without modal
          return;
        }
      } catch ( err ) {
        logger.warn( 'Could not compare grouped settings on navigation attempt', err );
        return;
      }

      detail.handled = true;
      setPendingHref( detail.path );
      setIsConfirmLeaveOpen( true );
    };

    checkCanSave();

    window.addEventListener( 'app:navigateAttempt', onNavigateAttempt as EventListener );
    return () => { return window.removeEventListener( 'app:navigateAttempt', onNavigateAttempt as EventListener ); };
  }, [ groupedSettings ] );


  const inits = async () => {
    try {
      const clientsFetched = await CommonClientService.getAllowedClients( null, token, dispatch );
      setAllClients( clientsFetched );
      if ( selectedClientId && GlobalService.isValidGuid( selectedClientId ) ) {
        const sitesForClient = await CommonSiteService.getAllowedSitesByClientsId( [ selectedClientId ], token, dispatch );
        setAllSites( sitesForClient );
      } else {
        setAllSites( [] );
      }
      if ( selectedSiteId && GlobalService.isValidGuid( selectedSiteId ) ) {
        const sitesForSite = await CommonSectorService.getAllowedSectorsBySitesId( [ selectedSiteId ], token, dispatch );
        setAllSectors( sitesForSite );
      } else {
        setAllSectors( [] );
      }
    } catch ( e ) {
      logger.error( 'Error during inits():', e );
      setAllClients( [] );
      setAllSites( [] );
      setAllSectors( [] );
    } finally {
      setIsFirstRender( false );
    }
  };


  const buildSettingsSnapshot = ( grouped: { calendar: GlobalSetting[]; eventManager: GlobalSetting[] } ) => {
    const normalizeValue = ( v: GlobalSetting['value'] | null | undefined ): string => {
      if ( v === null || v === undefined ) { return ''; }
      if ( typeof v === 'boolean' ) { return v ? '1' : '0'; }
      if ( typeof v === 'number' ) { return String( v ); }
      if ( typeof v === 'string' ) { return v.trim(); }
      try { return JSON.stringify( v ); } catch { return String( v ); }
    };

    try {
      const normalize = ( arr: GlobalSetting[] ) => {return arr
        .map( s => {return { keyframe: s.keyframe, value: normalizeValue( s.value ) }; } )
        .sort( ( a, b ) => {return a.keyframe.localeCompare( b.keyframe );} );};

      const normalized = {
        calendar: normalize( grouped.calendar || [] ),
        eventManager: normalize( grouped.eventManager || [] ),
      };
      return JSON.stringify( normalized );
    } catch ( e ) {
      logger.warn( 'Could not build settings snapshot', e );
      return '';
    }
  };

  const isValidClientId = (): boolean => {
    return selectedClientId ? GlobalService.isValidGuid( selectedClientId ) : false;
  };

  const toggleClientSelectorPopup = useCallback( () => {
    if ( allClients.length > 1 ) {
      setClientSelectorPopupVisible( prev => { return !prev; } );
    }
  }, [ allClients ] );

  const handleClientClick = ( client: Client ) => {
    setSelectedClientId( client.id );
    setClientSelectorPopupVisible( false );
  };

  const getSelectedSiteIndex = () => {
    if ( !selectedSiteId || allSites.length === 0 ) {
      return -1;
    }
    return allSites.findIndex( site => {return site.id === selectedSiteId;} );
  };

  const handleSiteClick = ( siteIndex: number ) => {
    if ( allSites && allSites.length > siteIndex && siteIndex >= 0 ) {
      setSelectedSiteId( allSites[ siteIndex ]?.id );
    }
  };

  const getSelectedSectorIndex = () => {
    if ( !selectedSectorId || allSectors.length === 0 ) {
      return -1;
    }
    return allSectors.findIndex( sector => {return sector.id === selectedSectorId;} );
  };

  const handleSectorClick = ( sectorIndex: number ) => {
    if ( allSectors && allSectors.length > sectorIndex && sectorIndex >= 0 ) {
      setSelectedSectorId( allSectors[ sectorIndex ]?.id );
    }
  };

  const handleSave = async () => {
    if ( !isValidClientId() ) {
      toast.error( t( 'common:no_client_selected' ) );
      return;
    }

    setIsSaving( true );
    try {
      const allSettings = [
        ...groupedSettings.calendar,
        ...groupedSettings.eventManager,
      ];

      for ( const setting of allSettings ) {
        await GlobalSettingsService.updateGlobalSettingById(
          setting.id,
          setting.keyframe,
          typeof setting.value === 'boolean' ? ( setting.value ? 1 : 0 ) : String( setting.value ),
          setting.type,
          selectedClientId!,
          token,
          dispatch
        );
      }

      setDefaultGroupedSettings( structuredClone( groupedSettings ) );
      setCanSaving( false );
      toast.success( t( 'common:saved' ) );
      try {
        initialGroupedSettingsSnapshotRef.current = buildSettingsSnapshot( groupedSettings );
      } catch ( e ) {
        logger.warn( 'Could not snapshot grouped settings after save', e );
      }
    } catch ( error ) {
      logger.error( 'Error saving settings:', error );
      toast.error( t( 'common:error.saving_settings' ) );
    } finally {
      setIsSaving( false );
    }
  };

  const handleTabSelect = ( index: number ) => {
    setSelectedPage( index );
  };

  const checkCanSave = () => {

    const allGroupedSettings = [ ...groupedSettings.calendar, ...groupedSettings.eventManager ];
    const allDefaultGroupedSettings = [ ...defaultGroupedSettings.calendar, ...defaultGroupedSettings.eventManager ];
    let result = false;
    let required = true;

    for ( const setting of allGroupedSettings ) {
      const defaultSetting = allDefaultGroupedSettings.find( item => { return item.id === setting.id; } );
      if ( setting.keyframe == KEYFRAME.DAYS_DISPLAY && typeof ( setting.value ) === 'number' ) {
        required = setting.value > 0;
      }
      result = result || setting.value != defaultSetting?.value;
    }

    setCanSaving( result && required );
  };

  const handleConfirmLeave = () => {
    setIsConfirmLeaveOpen( false );
    const href = pendingHref;
    setPendingHref( null );
    if ( !href ) { return; }
    try {
      const url = new URL( href );
      const path = url.pathname + url.search + url.hash;
      navigate( path );
    } catch {
      window.location.href = href;
    }
  };

  const handleCancelLeave = () => {
    setIsConfirmLeaveOpen( false );
    setPendingHref( null );
  };

  return (
    <Page title={t( 'common:settings' )}>
      {
        isFirstRender ?
          <Loading size={ 100 }/>
          :
          <>
            <DataChecker clients={allClients}>
              <>
                {pages.length === 0 ? null : (
                  <>
                    <Tabs items={pages} activeItem={selectedPage} onItemClick={handleTabSelect}/>
                    <div className="flex flex-col gap-6 p-5 border rounded-xl">
                      {
                        ( pages[ selectedPage ]?.id === 'globalSettings' && isClientAdmin && selectedClientId && me ) && (
                          <>
                            <div className="p-4 mb-6 rounded-xl border bg-blue-50 flex items-center justify-start">
                              <ClientSelector
                                selected={allClients.find( c => { return c.id === selectedClientId; } ) || null}
                                otherClients={allClients.filter( c => { return c.id !== selectedClientId; } )}
                                toggle={toggleClientSelectorPopup}
                                showPopup={isClientSelectorPopupVisible}
                                onClientClick={ client => { handleClientClick( client ); } }
                                popupRef={popupRef}
                                buttonRef={buttonRef}
                                isClickable={allClients.length > 1}
                              />
                            </div>
                            <SettingsClient
                              clientId={selectedClientId}
                              groupedSettings={groupedSettings}
                              setGroupedSettings={setGroupedSettings}
                              onSave={handleSave}
                              isSaving={isSaving}
                              canSave={canSave}
                            />
                          </>
                        )
                      }
                      {
                        ( pages[ selectedPage ]?.id === 'site_settings' && isSiteAdmin && selectedSiteId && me ) && (
                          <div>
                            <div className="p-4 mb-4 rounded-xl border bg-green-50">
                              <div className="flex items-center justify-start">
                                <ClientSelector
                                  selected={allClients.find( c => { return c.id === selectedClientId; } ) || null}
                                  otherClients={ allClients.filter( client => { return client.id !== selectedClientId; } ) }
                                  toggle={toggleClientSelectorPopup}
                                  showPopup={isClientSelectorPopupVisible}
                                  onClientClick={ client => { handleClientClick( client ); } }
                                  popupRef={popupRef}
                                  buttonRef={buttonRef}
                                  isClickable={allClients.length > 1}
                                  className="mr-4"
                                />
                              </div>
                              <div className="mt-3">
                                <Tabs
                                  items={ allSites .filter( site => { return CommonGlobalService.isAdminOfSite( me, site.id ); } ) }
                                  activeItem={getSelectedSiteIndex()}
                                  onItemClick={handleSiteClick}
                                />
                              </div>
                            </div>

                            <SettingsSite
                              key={'settingsSite'}
                              selectedSiteId={selectedSiteId}
                              customSettings={customSiteSettings}
                              clients={ allClients }
                              sites={ allSites }
                              setCustomSettings={setCustomSiteSettings}
                            />
                          </div>
                        )
                      }
                      {
                        ( pages[ selectedPage ]?.id === 'sector_settings' && isSectorAdmin && selectedSectorId && me ) && (
                          <div>
                            <div className="p-4 mb-4 rounded-xl border bg-green-50">
                              <div className="flex items-center justify-start">
                                <ClientSelector
                                  selected={ allClients.find( c => { return c.id === selectedClientId; } ) || null }
                                  otherClients={ allClients .filter( client => { return client.id !== selectedClientId; } ) }
                                  toggle={toggleClientSelectorPopup}
                                  showPopup={isClientSelectorPopupVisible}
                                  onClientClick={ client => { handleClientClick( client ); } }
                                  popupRef={popupRef}
                                  buttonRef={buttonRef}
                                  isClickable={allClients.length > 1}
                                  className="mr-4"
                                />
                              </div>
                              <div className='flex flex-col gap-2 mt-3 w-fit justify-self-center'>
                                <Tabs
                                  items={ allSites }
                                  activeItem={getSelectedSiteIndex()}
                                  onItemClick={handleSiteClick}
                                />
                                <div className='h-0.5 bg-gray-200 mt-3 -mx-8'/>
                                <Tabs
                                  items={ allSectors.filter( sector => { return CommonGlobalService.isAdminOfSector( me, sector.id ); } ) }
                                  activeItem={getSelectedSectorIndex()}
                                  onItemClick={handleSectorClick}
                                />
                              </div>
                            </div>

                            <SettingsSector
                              key={'settingsSector'}
                              selectedSectorId={selectedSectorId}
                            />
                          </div>
                        )
                      }
                    </div>
                  </>
                )}
              </>
            </DataChecker>
            <ConfirmationModal
              isOpen={isConfirmLeaveOpen}
              onConfirm={handleConfirmLeave}
              onCancel={handleCancelLeave}
              title={t( 'common:confirm_navigation' )}
              description={t( 'common:are_you_sure_you_want_to_leave' )}
            />
          </>
      }
    </Page>
  );
};

export default ParametersView;
